import type { ExerciseDef } from './types'

// ─── 10.2 Fine-tuning & LoRA ──────────────────────────────────────────────────

export const loraCount: ExerciseDef = {
  starter: `// A layer's weight matrix W is dIn × dOut. LoRA freezes W and learns B·A instead:
//   A: dIn × r     B: r × dOut     (so x·A·B has the same shape as x·W)

// 1. loraParams(dIn, dOut, r): how many numbers LoRA trains for this layer.
function loraParams(dIn: number, dOut: number, r: number): number {
  return 0
}

// 2. share(dIn, dOut, r): LoRA's trainable numbers as a fraction of the full matrix.
function share(dIn: number, dOut: number, r: number): number {
  return 1
}

console.log("4096 × 4096 layer, rank 8:", loraParams(4096, 4096, 8), "numbers =", round(100 * share(4096, 4096, 8), 2), "% of", 4096 * 4096)
`,
  solution: `function loraParams(dIn: number, dOut: number, r: number): number {
  return r * (dIn + dOut)
}

function share(dIn: number, dOut: number, r: number): number {
  return loraParams(dIn, dOut, r) / (dIn * dOut)
}

console.log("4096 × 4096 layer, rank 8:", loraParams(4096, 4096, 8), "numbers =", round(100 * share(4096, 4096, 8), 2), "% of", 4096 * 4096)
`,
  tests: `
test("rank 8 on 4096 × 4096 trains 65 536 numbers", () => expect(loraParams(4096, 4096, 8), "loraParams").toBe(65536))
test("…which is under 0.4% of the layer", () => expect(share(4096, 4096, 8), "share").toBeCloseTo(65536 / 16777216))
test("works for non-square layers", () => expect(loraParams(1000, 10, 2), "loraParams(1000, 10, 2)").toBe(2020))
`,
}

export const loraTrain: ExerciseDef = {
  starter: `// A "pretrained" layer W maps 6 inputs to 4 outputs. The new task needs a slightly different layer —
// one that differs from W by a rank-1 change. Learn that change with LoRA, keeping W frozen.
random.seed(5)
const W = Tensor.randn(6, 4)                                         // pretrained, frozen
const u = Tensor.randn(6, 1), v = Tensor.randn(1, 4)
const Wnew = W.add(u.matmul(v).mul(0.5))                              // what the new task really needs
const X = Tensor.randn(64, 6)
const Y = X.matmul(Wnew)                                              // the new task's targets

const r = 2
const A = Tensor.randn(6, r, 0.1)       // trainable
const B = Tensor.zeros(r, 4)            // trainable — starts at zero, so training starts from W exactly
const Wbefore = Array.from(W.data)

// forward(x): the frozen layer plus the low-rank update:  x·W + x·A·B
function forward(x: Tensor): Tensor {
  return x.matmul(W)
}

// Only A and B go to the optimiser. W never changes.
const opt = new Adam([A, B], { lr: 0.03 })
const chart = plot.live("loss on the new task")
for (let step = 0; step < 300; step++) {
  const loss = forward(X).mse(Y)
  opt.zeroGrad()
  loss.backward()
  opt.step()
  if (step % 10 === 0) chart.push(loss.item())
}
const finalLoss = forward(X).mse(Y).item()
console.log("final loss:", finalLoss)
`,
  solution: `random.seed(5)
const W = Tensor.randn(6, 4)
const u = Tensor.randn(6, 1), v = Tensor.randn(1, 4)
const Wnew = W.add(u.matmul(v).mul(0.5))
const X = Tensor.randn(64, 6)
const Y = X.matmul(Wnew)

const r = 2
const A = Tensor.randn(6, r, 0.1)
const B = Tensor.zeros(r, 4)
const Wbefore = Array.from(W.data)

function forward(x: Tensor): Tensor {
  return x.matmul(W).add(x.matmul(A).matmul(B))
}

const opt = new Adam([A, B], { lr: 0.03 })
const chart = plot.live("loss on the new task")
for (let step = 0; step < 300; step++) {
  const loss = forward(X).mse(Y)
  opt.zeroGrad()
  loss.backward()
  opt.step()
  if (step % 10 === 0) chart.push(loss.item())
}
const finalLoss = forward(X).mse(Y).item()
console.log("final loss:", finalLoss)
`,
  tests: `
test("the frozen weights never changed", () => expect(Array.from(W.data), "W").toEqual(Wbefore))
test("the low-rank update learned the new task", () => expect(finalLoss, "final loss").toBeLessThan(1e-3))
test("B moved away from zero", () => expect(Array.from(B.data).some(x => Math.abs(x) > 0.01), "B is non-zero").toBe(true))
`,
}

export const chatTemplate: ExerciseDef = {
  starter: `// Fine-tuning a chat model starts with turning conversations into one long text, using special
// tokens to mark who's speaking. A common style:
//   <|system|>You are helpful.<|end|>\\n<|user|>Hi!<|end|>\\n<|assistant|>Hello!<|end|>\\n
type Message = { role: "system" | "user" | "assistant"; content: string }

// 1. formatChat(messages): one string in the style above (each message on its own line).
function formatChat(messages: Message[]): string {
  return ""
}

// 2. trainOn(messages): during fine-tuning, the loss only counts the ASSISTANT's tokens —
//    the model should learn to answer, not to imitate users. Return true for each message to learn from.
function trainOn(messages: Message[]): boolean[] {
  return messages.map(() => true)
}

const convo: Message[] = [
  { role: "system", content: "You are a helpful tutor." },
  { role: "user", content: "What is 2 + 2?" },
  { role: "assistant", content: "4." },
]
console.log(formatChat(convo))
console.log(trainOn(convo))
`,
  solution: `type Message = { role: "system" | "user" | "assistant"; content: string }

function formatChat(messages: Message[]): string {
  return messages.map(m => \`<|\${m.role}|>\${m.content}<|end|>\\n\`).join("")
}

function trainOn(messages: Message[]): boolean[] {
  return messages.map(m => m.role === "assistant")
}

const convo: Message[] = [
  { role: "system", content: "You are a helpful tutor." },
  { role: "user", content: "What is 2 + 2?" },
  { role: "assistant", content: "4." },
]
console.log(formatChat(convo))
console.log(trainOn(convo))
`,
  tests: `
test("formats a one-message chat", () => expect(formatChat([{ role: "user", content: "Hi!" }]), "formatChat").toBe("<|user|>Hi!<|end|>\\n"))
test("formats several messages in order", () => expect(formatChat([{ role: "system", content: "S" }, { role: "assistant", content: "A" }]), "formatChat").toBe("<|system|>S<|end|>\\n<|assistant|>A<|end|>\\n"))
test("only the assistant is trained on", () => expect(trainOn([{ role: "system", content: "" }, { role: "user", content: "" }, { role: "assistant", content: "" }]), "trainOn").toEqual([false, false, true]))
`,
}

// ─── 10.3 Retrieval & RAG ─────────────────────────────────────────────────────

const STOPWORDS = `const STOP = new Set("a an the and or of to in on is are was it its by for with as at from that this be can into their they them then than how what why which who does do".split(" "))`

export const chunking: ExerciseDef = {
  starter: `// Long documents are cut into overlapping chunks before indexing, so that each search result is
// small enough to paste into a prompt — and nothing important is split exactly at a boundary.

// chunk(words, size, overlap): windows of "size" words; each starts (size − overlap) words after the last.
// Stop once a window reaches the end. chunk(["a","b","c","d","e"], 3, 1) → [["a","b","c"], ["c","d","e"]]
function chunk(words: string[], size: number, overlap: number): string[][] {
  return [words]
}

const text = data.documents.map(d => d.text).join(" ").split(" ")
const chunks = chunk(text, 40, 10)
console.log(text.length, "words →", chunks.length, "chunks; first:", chunks[0].join(" "))
`,
  solution: `function chunk(words: string[], size: number, overlap: number): string[][] {
  const out: string[][] = []
  const step = size - overlap
  for (let start = 0; ; start += step) {
    out.push(words.slice(start, start + size))
    if (start + size >= words.length) break
  }
  return out
}

const text = data.documents.map(d => d.text).join(" ").split(" ")
const chunks = chunk(text, 40, 10)
console.log(text.length, "words →", chunks.length, "chunks; first:", chunks[0].join(" "))
`,
  tests: `
test("chunk(a…e, 3, 1)", () => expect(chunk(["a", "b", "c", "d", "e"], 3, 1), "chunk").toEqual([["a", "b", "c"], ["c", "d", "e"]]))
test("the last chunk may be shorter", () => expect(chunk(["a", "b", "c", "d"], 3, 0), "chunk").toEqual([["a", "b", "c"], ["d"]]))
test("short text is one chunk", () => expect(chunk(["x", "y"], 5, 2), "chunk").toEqual([["x", "y"]]))
test("consecutive chunks overlap by exactly 'overlap' words", () => {
  const __c = chunk(range(20).map(String), 6, 2)
  expect(__c[1].slice(0, 2), "start of chunk 2").toEqual(__c[0].slice(-2))
})
`,
}

export const tfidfSearch: ExerciseDef = {
  starter: `const docs = data.documents
${STOPWORDS}
const tokenize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").split(" ").filter(w => w && !STOP.has(w))

// TF-IDF: a word counts more if it's frequent in THIS document (term frequency)
// and rare across ALL documents (inverse document frequency).

// 1. idf(word) = log((N + 1) / (df + 1)) + 1, where N is the number of documents and df how many contain the word.
const docWords = docs.map(d => tokenize(d.title + " " + d.text))
function idf(word: string): number {
  return 1
}

// 2. vectorize(words): a Map from each word to (its count in words) × idf(word).
function vectorize(words: string[]): Map<string, number> {
  return new Map()
}

// 3. cosine(a, b) for two Maps (missing words count as 0), and search(query, k): the k best document titles.
function cosine(a: Map<string, number>, b: Map<string, number>): number {
  return 0
}
const docVecs = docWords.map(vectorize)
function search(query: string, k: number): string[] {
  return docs.slice(0, k).map(d => d.title)
}

for (const q of ["how do plants make sugar", "why does my learning rate diverge", "what causes ocean tides"]) console.log(q, "→", search(q, 2))
`,
  solution: `const docs = data.documents
${STOPWORDS}
const tokenize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").split(" ").filter(w => w && !STOP.has(w))

const docWords = docs.map(d => tokenize(d.title + " " + d.text))
function idf(word: string): number {
  const df = docWords.filter(ws => ws.includes(word)).length
  return Math.log((docs.length + 1) / (df + 1)) + 1
}

function vectorize(words: string[]): Map<string, number> {
  const m = new Map<string, number>()
  for (const w of words) m.set(w, (m.get(w) ?? 0) + 1)
  for (const [w, c] of m) m.set(w, c * idf(w))
  return m
}

function cosine(a: Map<string, number>, b: Map<string, number>): number {
  let dot = 0
  for (const [w, v] of a) dot += v * (b.get(w) ?? 0)
  const norm = (m: Map<string, number>) => Math.sqrt([...m.values()].reduce((s, v) => s + v * v, 0))
  const n = norm(a) * norm(b)
  return n ? dot / n : 0
}
const docVecs = docWords.map(vectorize)
function search(query: string, k: number): string[] {
  const q = vectorize(tokenize(query))
  return range(docs.length)
    .sort((i, j) => cosine(q, docVecs[j]) - cosine(q, docVecs[i]))
    .slice(0, k)
    .map(i => docs[i].title)
}

for (const q of ["how do plants make sugar", "why does my learning rate diverge", "what causes ocean tides"]) console.log(q, "→", search(q, 2))
`,
  tests: `
test("rare words weigh more than common ones", () => expect(idf("photosynthesis") > idf("model"), "idf(photosynthesis) > idf(model)").toBe(true))
test("vectorize counts words, weighted by idf", () => expect(vectorize(["moon", "moon"]).get("moon"), "weight of moon").toBeCloseTo(2 * idf("moon")))
test("cosine of a vector with itself is 1", () => expect(cosine(new Map([["a", 1], ["b", 2]]), new Map([["a", 1], ["b", 2]])), "cosine").toBeCloseTo(1))
test("finds Photosynthesis for plants and sugar", () => expect(search("how do plants make sugar", 1), "search").toEqual(["Photosynthesis"]))
test("finds Gradient descent for learning rates", () => expect(search("why does my learning rate diverge", 1), "search").toEqual(["Gradient descent"]))
test("finds The Moon for tides", () => expect(search("what causes ocean tides", 1), "search").toEqual(["The Moon"]))
`,
}

export const ragPrompt: ExerciseDef = {
  starter: `// 1. buildPrompt(question, sources): the text the language model will see. Number the sources [1], [2], …
//    Format, exactly:
//      Answer using only these sources. Cite them like [1].
//      [1] <title>: <text>
//      [2] <title>: <text>
//      Question: <question>
function buildPrompt(question: string, sources: { title: string; text: string }[]): string {
  return question
}

// 2. recallAtK(results, relevant): the fraction of queries whose relevant document appears in their results.
function recallAtK(results: string[][], relevant: string[]): number {
  return 0
}

const docs = data.documents
console.log(buildPrompt("What makes sourdough rise?", [docs[7]]))
console.log(recallAtK([["A", "B"], ["C", "D"], ["E", "F"]], ["B", "X", "E"]))   // 2 of 3 found
`,
  solution: `function buildPrompt(question: string, sources: { title: string; text: string }[]): string {
  const lines = ["Answer using only these sources. Cite them like [1]."]
  sources.forEach((s, i) => lines.push(\`[\${i + 1}] \${s.title}: \${s.text}\`))
  lines.push(\`Question: \${question}\`)
  return lines.join("\\n")
}

function recallAtK(results: string[][], relevant: string[]): number {
  return results.filter((r, i) => r.includes(relevant[i])).length / results.length
}

const docs = data.documents
console.log(buildPrompt("What makes sourdough rise?", [docs[7]]))
console.log(recallAtK([["A", "B"], ["C", "D"], ["E", "F"]], ["B", "X", "E"]))
`,
  tests: `
test("the prompt has the instruction, numbered sources and the question", () => expect(buildPrompt("Q?", [{ title: "T1", text: "one" }, { title: "T2", text: "two" }]), "prompt").toBe("Answer using only these sources. Cite them like [1].\\n[1] T1: one\\n[2] T2: two\\nQuestion: Q?"))
test("no sources: just the instruction and the question", () => expect(buildPrompt("Q?", []).split("\\n"), "lines").toHaveLength(2))
test("recall counts queries whose answer was retrieved", () => expect(recallAtK([["A", "B"], ["C", "D"], ["E", "F"]], ["B", "X", "E"]), "recall").toBeCloseTo(2 / 3))
`,
}

// ─── 10.6 Multimodal ──────────────────────────────────────────────────────────

export const patchify: ExerciseDef = {
  starter: `// A vision transformer reads an image as a sequence of patches.
// patchify(img, p): cut an H×W image into p×p patches, left to right, top to bottom;
// flatten each patch row by row into one token of p·p numbers.
function patchify(img: number[][], p: number): number[][] {
  return []
}

const img = data.image()            // 24 × 24
const tokens = patchify(img, 6)
console.log(tokens.length, "tokens of", tokens[0]?.length, "numbers each")
`,
  solution: `function patchify(img: number[][], p: number): number[][] {
  const out: number[][] = []
  for (let r = 0; r + p <= img.length; r += p) {
    for (let c = 0; c + p <= img[0].length; c += p) {
      const t: number[] = []
      for (let i = 0; i < p; i++) for (let j = 0; j < p; j++) t.push(img[r + i][c + j])
      out.push(t)
    }
  }
  return out
}

const img = data.image()
const tokens = patchify(img, 6)
console.log(tokens.length, "tokens of", tokens[0]?.length, "numbers each")
`,
  tests: `
test("a 24 × 24 image in 6 × 6 patches is 16 tokens of 36", () => {
  const __t = patchify(data.image(), 6)
  expect([__t.length, __t[0].length], "[tokens, size]").toEqual([16, 36])
})
test("patches go left to right, then down", () => expect(patchify([[1, 2, 3, 4], [5, 6, 7, 8], [9, 10, 11, 12], [13, 14, 15, 16]], 2), "patchify").toEqual([[1, 2, 5, 6], [3, 4, 7, 8], [9, 10, 13, 14], [11, 12, 15, 16]]))
`,
}

export const contrastive: ExerciseDef = {
  starter: `// CLIP's loss. S[i][j] = similarity of image i and caption j; the true pairs are on the diagonal.
// Treat each ROW as a classification ("which caption goes with image i?") and each COLUMN too
// ("which image goes with caption j?"). The loss is the average cross-entropy of both, with scores S / tau.
function clipLoss(S: number[][], tau: number): number {
  return 0
}

// A batch of 4 where the model has learned a lot vs. nothing at all:
const good = [[0.9, 0.1, 0.0, 0.2], [0.1, 0.8, 0.1, 0.0], [0.0, 0.2, 0.9, 0.1], [0.1, 0.0, 0.1, 0.8]]
const clueless = good.map(r => r.map(() => 0.3))
console.log("trained:", clipLoss(good, 0.1), "  untrained:", clipLoss(clueless, 0.1), "  log 4 =", Math.log(4))
`,
  solution: `function clipLoss(S: number[][], tau: number): number {
  const n = S.length
  const ce = (scores: number[], target: number) => -Math.log(vec.softmax(scores.map(s => s / tau))[target])
  let total = 0
  for (let i = 0; i < n; i++) {
    total += ce(S[i], i)
    total += ce(S.map(row => row[i]), i)
  }
  return total / (2 * n)
}

const good = [[0.9, 0.1, 0.0, 0.2], [0.1, 0.8, 0.1, 0.0], [0.0, 0.2, 0.9, 0.1], [0.1, 0.0, 0.1, 0.8]]
const clueless = good.map(r => r.map(() => 0.3))
console.log("trained:", clipLoss(good, 0.1), "  untrained:", clipLoss(clueless, 0.1), "  log 4 =", Math.log(4))
`,
  tests: `
test("all similarities equal → loss = log n", () => expect(clipLoss([[0.3, 0.3], [0.3, 0.3]], 0.1), "loss").toBeCloseTo(Math.log(2)))
test("a strong diagonal gives a small loss", () => expect(clipLoss([[1, 0], [0, 1]], 0.05), "loss").toBeLessThan(1e-6))
test("rows and columns both count", () => {
  const __S = [[1, 0.9], [0, 0.2]]
  const __row = (-Math.log(vec.softmax([10, 9])[0]) - Math.log(vec.softmax([0, 2])[1])) / 2
  const __col = (-Math.log(vec.softmax([10, 0])[0]) - Math.log(vec.softmax([9, 2])[1])) / 2
  expect(clipLoss(__S, 0.1), "loss").toBeCloseTo((__row + __col) / 2)
})
`,
}

export const zeroShot: ExerciseDef = {
  starter: `// Once images and text share one embedding space, you can classify images into ANY labels,
// with no extra training: embed each label as text, and pick the closest one.
const labels = ["a photo of a dog", "a photo of a car", "a photo of food"]
const labelEmb = [[0.9, 0.1, 0.1], [0.1, 0.9, 0.2], [0.1, 0.2, 0.9]]       // pretend text embeddings
const images = [[0.8, 0.3, 0.1], [0.2, 0.1, 0.7], [0.0, 1.0, 0.3], [0.7, 0.0, 0.4]]   // pretend image embeddings
const truth = [0, 2, 1, 0]

// zeroShot(img): the index of the label whose embedding has the highest cosine similarity.
function zeroShot(img: number[]): number {
  return 0
}

const predictions = images.map(zeroShot)
console.log(predictions.map(i => labels[i]))
`,
  solution: `const labels = ["a photo of a dog", "a photo of a car", "a photo of food"]
const labelEmb = [[0.9, 0.1, 0.1], [0.1, 0.9, 0.2], [0.1, 0.2, 0.9]]
const images = [[0.8, 0.3, 0.1], [0.2, 0.1, 0.7], [0.0, 1.0, 0.3], [0.7, 0.0, 0.4]]
const truth = [0, 2, 1, 0]

function zeroShot(img: number[]): number {
  return vec.argmax(labelEmb.map(l => vec.cosine(img, l)))
}

const predictions = images.map(zeroShot)
console.log(predictions.map(i => labels[i]))
`,
  tests: `
test("classifies every pretend image correctly", () => expect(images.map(zeroShot), "predictions").toEqual(truth))
test("uses cosine, not raw dot product", () => expect(zeroShot([10, 0, 9.9]), "zeroShot of a long vector").toBe(0))
`,
}

// ─── 10.9 Evaluation ──────────────────────────────────────────────────────────

export const passAtK: ExerciseDef = {
  starter: `// Code benchmarks: generate n programs per problem, c of them pass the tests.
// pass@k = the chance that at least one of k randomly picked samples passes:
//   1 − C(n − c, k) / C(n, k)        (C = "n choose k")
// Compute the ratio as a running product so the numbers never get huge:
//   C(n − c, k) / C(n, k) = Π for i = 0 … k−1 of (n − c − i) / (n − i)
function passAtK(n: number, c: number, k: number): number {
  return 0
}

console.log("10 samples, 3 correct:", [1, 5, 10].map(k => \`pass@\${k} = \${round(passAtK(10, 3, k), 3)}\`).join(", "))
`,
  solution: `function passAtK(n: number, c: number, k: number): number {
  if (n - c < k) return 1
  let ratio = 1
  for (let i = 0; i < k; i++) ratio *= (n - c - i) / (n - i)
  return 1 - ratio
}

console.log("10 samples, 3 correct:", [1, 5, 10].map(k => \`pass@\${k} = \${round(passAtK(10, 3, k), 3)}\`).join(", "))
`,
  tests: `
test("pass@1 is just the success rate", () => expect(passAtK(10, 3, 1), "pass@1").toBeCloseTo(0.3))
test("pass@k with k = n is certain if any passed", () => expect(passAtK(10, 1, 10), "pass@10").toBeCloseTo(1))
test("no correct samples → 0", () => expect(passAtK(20, 0, 5), "pass@5").toBe(0))
test("10 samples, 3 correct: pass@5 = 1 − 21/252", () => expect(passAtK(10, 3, 5), "pass@5").toBeCloseTo(1 - 21 / 252))
`,
}

export const bootstrap: ExerciseDef = {
  starter: `// Model A and B answered the same 200 questions (1 = right). B scores higher — but is it really better?
random.seed(4)
const A = range(200).map(() => (random.random() < 0.70 ? 1 : 0))
const B = range(200).map(() => (random.random() < 0.74 ? 1 : 0))

// bootstrapInterval(a, b, iters): resample question INDICES with replacement (same indices for both
// models), record mean(b) − mean(a) each time, sort, and return the middle 95%: [2.5th percentile, 97.5th].
function bootstrapInterval(a: number[], b: number[], iters = 2000): [number, number] {
  return [0, 0]
}

const [lo, hi] = bootstrapInterval(A, B)
console.log("B − A:", round(vec.mean(B) - vec.mean(A), 3), "  95% interval:", [round(lo, 3), round(hi, 3)])
console.log(lo > 0 ? "B is better." : "Can't tell — the interval includes 0.")
`,
  solution: `random.seed(4)
const A = range(200).map(() => (random.random() < 0.70 ? 1 : 0))
const B = range(200).map(() => (random.random() < 0.74 ? 1 : 0))

function bootstrapInterval(a: number[], b: number[], iters = 2000): [number, number] {
  const n = a.length
  const diffs: number[] = []
  for (let t = 0; t < iters; t++) {
    let sa = 0, sb = 0
    for (let i = 0; i < n; i++) {
      const j = random.int(0, n)
      sa += a[j]
      sb += b[j]
    }
    diffs.push((sb - sa) / n)
  }
  diffs.sort((x, y) => x - y)
  return [diffs[Math.floor(iters * 0.025)], diffs[Math.floor(iters * 0.975)]]
}

const [lo, hi] = bootstrapInterval(A, B)
console.log("B − A:", round(vec.mean(B) - vec.mean(A), 3), "  95% interval:", [round(lo, 3), round(hi, 3)])
console.log(lo > 0 ? "B is better." : "Can't tell — the interval includes 0.")
`,
  tests: `
test("identical models → the interval is [0, 0]", () => expect(bootstrapInterval([1, 0, 1, 1], [1, 0, 1, 1]), "interval").toEqual([0, 0]))
test("a huge, consistent gap is clearly positive", () => expect(bootstrapInterval(new Array(100).fill(0), new Array(100).fill(1))[0], "lower end").toBe(1))
test("the interval contains the observed difference", () => {
  const __d = vec.mean(B) - vec.mean(A)
  expect(lo <= __d && __d <= hi, "lo ≤ observed ≤ hi").toBe(true)
})
test("with 200 questions, a ~4-point gap is NOT clearly significant", () => expect(lo, "lower end").toBeLessThan(0.02))
`,
}

export const elo: ExerciseDef = {
  starter: `// Chatbot arenas rank models from head-to-head votes with Elo ratings (invented for chess).
// 1. expected(ra, rb): the chance A beats B:  1 / (1 + 10^((rb − ra) / 400))
function expected(ra: number, rb: number): number {
  return 0.5
}

// 2. update(ra, rb, aWon, K): new ratings after one game.  A gains K × (result − expected), B loses the same.
//    result is 1 if A won, 0 if it lost.
function update(ra: number, rb: number, aWon: boolean, K = 32): [number, number] {
  return [ra, rb]
}

// A small arena: model X wins 70% of its games against Y.
random.seed(1)
let x = 1000, y = 1000
for (let game = 0; game < 500; game++) [x, y] = update(x, y, random.random() < 0.7, 16)
console.log("X:", round(x), " Y:", round(y), " → X expected to win", round(expected(x, y), 2))
`,
  solution: `function expected(ra: number, rb: number): number {
  return 1 / (1 + 10 ** ((rb - ra) / 400))
}

function update(ra: number, rb: number, aWon: boolean, K = 32): [number, number] {
  const delta = K * ((aWon ? 1 : 0) - expected(ra, rb))
  return [ra + delta, rb - delta]
}

random.seed(1)
let x = 1000, y = 1000
for (let game = 0; game < 500; game++) [x, y] = update(x, y, random.random() < 0.7, 16)
console.log("X:", round(x), " Y:", round(y), " → X expected to win", round(expected(x, y), 2))
`,
  tests: `
test("equal ratings → 50/50", () => expect(expected(1500, 1500), "expected").toBeCloseTo(0.5))
test("400 points ahead → 10 to 1", () => expect(expected(1400, 1000), "expected").toBeCloseTo(10 / 11))
test("an upset moves ratings a lot", () => expect(update(1000, 1400, true, 32)[0], "A's new rating").toBeCloseTo(1000 + 32 * (1 - 1 / 11)))
test("points are only exchanged, never created", () => {
  const [__a, __b] = update(1234, 987, false, 24)
  expect(__a + __b, "total").toBeCloseTo(1234 + 987)
})
test("the arena recovers the 70% win rate", () => expect(expected(x, y), "expected(x, y)").toBeBetween(0.6, 0.8))
`,
}

export const contamination: ExerciseDef = {
  starter: `// If benchmark questions leaked into the training data, a high score proves nothing.
// A simple check: what fraction of the test text's word n-grams also appear in the training text?

// 1. ngrams(text, n): the set of all runs of n consecutive words (joined by single spaces), lowercased.
function ngrams(text: string, n: number): Set<string> {
  return new Set()
}

// 2. overlap(test, train, n): the fraction of the test's n-grams that also appear in the training text.
function overlap(test: string, train: string, n: number): number {
  return 0
}

const train = data.documents.map(d => d.text).join(" ")
const leaked = "The Moon orbits Earth about once every 27 days."
const fresh = "Octopuses have three hearts and blue blood."
console.log("leaked question:", overlap(leaked, train, 3), "  fresh question:", overlap(fresh, train, 3))
`,
  solution: `function ngrams(text: string, n: number): Set<string> {
  const words = text.toLowerCase().split(/\\s+/).filter(w => w)
  const out = new Set<string>()
  for (let i = 0; i + n <= words.length; i++) out.add(words.slice(i, i + n).join(" "))
  return out
}

function overlap(test: string, train: string, n: number): number {
  const t = ngrams(test, n)
  if (t.size === 0) return 0
  const seen = ngrams(train, n)
  return [...t].filter(g => seen.has(g)).length / t.size
}

const train = data.documents.map(d => d.text).join(" ")
const leaked = "The Moon orbits Earth about once every 27 days."
const fresh = "Octopuses have three hearts and blue blood."
console.log("leaked question:", overlap(leaked, train, 3), "  fresh question:", overlap(fresh, train, 3))
`,
  tests: `
test("ngrams of 'a b c d' with n = 2", () => expect([...ngrams("a b c d", 2)], "bigrams").toEqual(["a b", "b c", "c d"]))
test("ngrams are lowercased", () => expect(ngrams("The Cat", 2).has("the cat"), "has 'the cat'").toBe(true))
test("a copied sentence overlaps completely", () => expect(overlap("the moon orbits earth", "we know the moon orbits earth well", 3), "overlap").toBe(1))
test("a new sentence doesn't overlap", () => expect(overlap("octopuses have three hearts", "the moon orbits earth", 3), "overlap").toBe(0))
`,
}

// ─── 10.10 Safety & alignment ─────────────────────────────────────────────────

export const calibration: ExerciseDef = {
  starter: `// A model is CALIBRATED if, among answers it gives with 80% confidence, about 80% are right.
// Expected calibration error (ECE): put predictions into 10 equal-width confidence bins
// (0–0.1, 0.1–0.2, …; a confidence of exactly 1 goes in the last bin). For each non-empty bin:
//   |average confidence − accuracy| × (bin size / total).   Add these up.
function ece(confidence: number[], correct: boolean[]): number {
  return 0
}

random.seed(9)
// An overconfident model: says 90% but is right only 60% of the time.
const conf = range(500).map(() => 0.85 + random.random() * 0.1)
const right = conf.map(() => random.random() < 0.6)
console.log("ECE:", round(ece(conf, right), 3))
`,
  solution: `function ece(confidence: number[], correct: boolean[]): number {
  const n = confidence.length
  let total = 0
  for (let b = 0; b < 10; b++) {
    const idx = range(n).filter(i => Math.min(9, Math.floor(confidence[i] * 10)) === b)
    if (!idx.length) continue
    const avgConf = vec.mean(idx.map(i => confidence[i]))
    const acc = idx.filter(i => correct[i]).length / idx.length
    total += Math.abs(avgConf - acc) * (idx.length / n)
  }
  return total
}

random.seed(9)
const conf = range(500).map(() => 0.85 + random.random() * 0.1)
const right = conf.map(() => random.random() < 0.6)
console.log("ECE:", round(ece(conf, right), 3))
`,
  tests: `
test("perfectly calibrated → 0", () => expect(ece([0.75, 0.75, 0.75, 0.75], [true, true, true, false]), "ece").toBeCloseTo(0))
test("always 100% sure, always wrong → 1", () => expect(ece([1, 1], [false, false]), "ece").toBeCloseTo(1))
test("bins are weighted by size", () => expect(ece([0.95, 0.15, 0.15, 0.15], [true, false, false, false]), "ece").toBeCloseTo(0.25 * 0.05 + 0.75 * 0.15))
test("the overconfident model has ECE around 0.3", () => expect(ece(conf, right), "ece").toBeBetween(0.22, 0.38))
`,
}

export const linearProbe: ExerciseDef = {
  starter: `// Interpretability: is a concept written somewhere inside a network's hidden activations?
// Train a tiny LINEAR classifier (a "probe") to read it out. If a straight line can find it, the network
// represents it. Here, a fixed network reads short "reviews" (bags of 6 words) whose hidden layer we inspect.
random.seed(2)
const good = ["great", "lovely", "superb"], bad = ["awful", "boring", "dreadful"], filler = ["film", "plot", "actor", "scene", "music", "ending"]
const vocab = [...good, ...bad, ...filler]
function review(positive: boolean): number[] {
  const words = range(6).map(() => (random.random() < 0.4 ? random.choice(positive ? good : bad) : random.choice(filler)))
  return vocab.map(v => words.filter(w => w === v).length)
}
const labels = range(300).map(i => i % 2)
const inputs = labels.map(l => review(l === 1))
const W1 = mat.random(vocab.length, 16, 0.6)                    // the network we're inspecting (frozen)
const hidden = inputs.map(x => mat.apply(mat.transpose(W1), x).map(Math.tanh))

// trainProbe(H, y): logistic regression on the hidden activations (the same code as in Module 6!).
// Return a function that predicts 0 or 1 from a hidden vector.
function trainProbe(H: number[][], y: number[]): (h: number[]) => number {
  return () => 0
}

const probe = trainProbe(hidden.slice(0, 200), labels.slice(0, 200))
const probeAccuracy = range(200, 300).filter(i => probe(hidden[i]) === labels[i]).length / 100
console.log("probe accuracy on new reviews:", probeAccuracy)
`,
  solution: `random.seed(2)
const good = ["great", "lovely", "superb"], bad = ["awful", "boring", "dreadful"], filler = ["film", "plot", "actor", "scene", "music", "ending"]
const vocab = [...good, ...bad, ...filler]
function review(positive: boolean): number[] {
  const words = range(6).map(() => (random.random() < 0.4 ? random.choice(positive ? good : bad) : random.choice(filler)))
  return vocab.map(v => words.filter(w => w === v).length)
}
const labels = range(300).map(i => i % 2)
const inputs = labels.map(l => review(l === 1))
const W1 = mat.random(vocab.length, 16, 0.6)
const hidden = inputs.map(x => mat.apply(mat.transpose(W1), x).map(Math.tanh))

function trainProbe(H: number[][], y: number[]): (h: number[]) => number {
  const sigmoid = (z: number) => 1 / (1 + Math.exp(-z))
  let w = new Array(H[0].length).fill(0)
  let b = 0
  for (let epoch = 0; epoch < 300; epoch++) {
    const gw = new Array(w.length).fill(0)
    let gb = 0
    H.forEach((h, i) => {
      const e = sigmoid(vec.dot(w, h) + b) - y[i]
      for (let j = 0; j < w.length; j++) gw[j] += (e * h[j]) / H.length
      gb += e / H.length
    })
    w = w.map((v, j) => v - 1 * gw[j])
    b -= 1 * gb
  }
  return h => (vec.dot(w, h) + b > 0 ? 1 : 0)
}

const probe = trainProbe(hidden.slice(0, 200), labels.slice(0, 200))
const probeAccuracy = range(200, 300).filter(i => probe(hidden[i]) === labels[i]).length / 100
console.log("probe accuracy on new reviews:", probeAccuracy)
`,
  tests: `
test("a linear probe reads the sentiment out of the hidden layer", () => expect(probeAccuracy, "probeAccuracy").toBeGreaterThan(0.85))
test("the probe returns 0 or 1", () => expect([0, 1].includes(probe(hidden[0])), "output").toBe(true))
`,
}

export const fairness: ExerciseDef = {
  starter: `// A loan model's decisions for two groups of applicants. "truth" = would actually repay (1) or not (0).
// One fairness check, "equal opportunity": among people who WOULD repay, does each group get approved equally often?

// 1. truePositiveRate(pred, truth): of those with truth 1, the fraction predicted 1.
function truePositiveRate(pred: number[], truth: number[]): number {
  return 0
}

// 2. opportunityGap(pred, truth, group): the absolute difference in true-positive rate between group "a" and group "b".
function opportunityGap(pred: number[], truth: number[], group: string[]): number {
  return 0
}

const truth = [1, 1, 1, 1, 0, 0, 1, 1, 1, 1, 0, 0]
const pred  = [1, 1, 1, 0, 0, 1, 1, 0, 0, 0, 0, 0]
const group = ["a", "a", "a", "a", "a", "a", "b", "b", "b", "b", "b", "b"]
console.log("gap:", opportunityGap(pred, truth, group))   // a: 3/4 approved, b: 1/4 approved
`,
  solution: `function truePositiveRate(pred: number[], truth: number[]): number {
  const pos = range(truth.length).filter(i => truth[i] === 1)
  return pos.filter(i => pred[i] === 1).length / pos.length
}

function opportunityGap(pred: number[], truth: number[], group: string[]): number {
  const rate = (g: string) => {
    const idx = range(group.length).filter(i => group[i] === g)
    return truePositiveRate(idx.map(i => pred[i]), idx.map(i => truth[i]))
  }
  return Math.abs(rate("a") - rate("b"))
}

const truth = [1, 1, 1, 1, 0, 0, 1, 1, 1, 1, 0, 0]
const pred  = [1, 1, 1, 0, 0, 1, 1, 0, 0, 0, 0, 0]
const group = ["a", "a", "a", "a", "a", "a", "b", "b", "b", "b", "b", "b"]
console.log("gap:", opportunityGap(pred, truth, group))
`,
  tests: `
test("true-positive rate ignores the negatives", () => expect(truePositiveRate([1, 0, 1, 1], [1, 1, 0, 0]), "TPR").toBeCloseTo(0.5))
test("the example's gap is 0.5", () => expect(opportunityGap([1, 1, 1, 0, 0, 1, 1, 0, 0, 0, 0, 0], [1, 1, 1, 1, 0, 0, 1, 1, 1, 1, 0, 0], ["a", "a", "a", "a", "a", "a", "b", "b", "b", "b", "b", "b"]), "gap").toBeCloseTo(0.5))
test("equal treatment → no gap", () => expect(opportunityGap([1, 0, 1, 0], [1, 1, 1, 1], ["a", "a", "b", "b"]), "gap").toBe(0))
`,
}
